import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { BookOpen, PlayCircle, CheckCircle, Calendar, Award, TrendingUp, Clock, ArrowRight, Gift, Copy, Share2, Users } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [progress, setProgress] = useState(null);
  const [quizHistory, setQuizHistory] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [referralInput, setReferralInput] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);
  const [myReferrals, setMyReferrals] = useState([]);

  useEffect(() => {
    const load = async () => {
      // Check if returning from payment
      if (searchParams.get("payment") === "success") {
        toast.success("Pago completado exitosamente!");
        await refreshUser();
      }

      try {
        const [progsRes, eventsRes, quizRes] = await Promise.all([
          fetch(`${API}/programs`, { credentials: "include" }),
          fetch(`${API}/events/upcoming`, { credentials: "include" }),
          fetch(`${API}/quizzes/user/all-history`, { credentials: "include" }),
        ]);
        if (progsRes.ok) setPrograms(await progsRes.json());
        if (eventsRes.ok) setEvents(await eventsRes.json());
        if (quizRes.ok) setQuizHistory(await quizRes.json());

        // Load referral data
        try {
          const [refCodeRes, refListRes] = await Promise.all([
            fetch(`${API}/referrals/my-code`, { credentials: "include" }),
            fetch(`${API}/referrals/my-referrals`, { credentials: "include" }),
          ]);
          if (refCodeRes.ok) setReferralData(await refCodeRes.json());
          if (refListRes.ok) setMyReferrals(await refListRes.json());
        } catch {}

        if (user?.program) {
          const progRes = await fetch(`${API}/programs/${user.program}/progress`, { credentials: "include" });
          if (progRes.ok) setProgress(await progRes.json());
        }
      } catch {}
    };
    load();
  }, [user]);

  const progressPct = progress ? (progress.total_lessons > 0 ? Math.round((progress.completed_lessons / progress.total_lessons) * 100) : 0) : 0;

  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      toast.success("Codigo copiado al portapapeles");
    }
  };

  const shareReferralLink = () => {
    const shareUrl = `${window.location.origin}/auth?ref=${referralData?.referral_code}`;
    if (navigator.share) {
      navigator.share({
        title: "Mi Prepa en Shorts",
        text: `Usa mi codigo ${referralData?.referral_code} y obtiene 10% de descuento en Mi Prepa en Shorts`,
        url: shareUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  const handleApplyReferral = async () => {
    if (!referralInput.trim()) return;
    setApplyingCode(true);
    try {
      const res = await fetch(`${API}/referrals/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referral_code: referralInput.trim() }),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setReferralInput("");
        // Reload referral data
        const refRes = await fetch(`${API}/referrals/my-code`, { credentials: "include" });
        if (refRes.ok) setReferralData(await refRes.json());
      } else {
        toast.error(data.detail || "Error aplicando el codigo");
      }
    } catch {
      toast.error("Error de conexion");
    }
    setApplyingCode(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }} data-testid="dashboard-welcome">
            Hola, {user?.name || "Estudiante"}
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.subscription_status === "active" ? "Miembro activo" : "Cuenta gratuita"} &bull; {user?.program ? programs.find(p => p.program_id === user.program)?.name || user.program : "Sin programa seleccionado"}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { icon: TrendingUp, label: "Progreso", value: `${progressPct}%`, color: "#20D3C0" },
            { icon: CheckCircle, label: "Lecciones completadas", value: progress?.completed_lessons || 0, color: "#22C55E" },
            { icon: BookOpen, label: "Quizzes realizados", value: quizHistory.length, color: "#193B6E" },
            { icon: Calendar, label: "Eventos proximos", value: events.length, color: "#F59E0B" },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between mb-3">
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                <span className="text-2xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>{stat.value}</span>
              </div>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress */}
            {user?.program && progress && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="progress-card">
                <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Tu progreso</h3>
                <Progress value={progressPct} className="h-3 mb-2" />
                <p className="text-sm text-slate-500">{progress.completed_lessons} de {progress.total_lessons} lecciones completadas</p>
                <Link
                  to={`/program/${user.program}`}
                  className="inline-flex items-center gap-1 mt-4 text-[#20D3C0] font-medium text-sm hover:underline"
                  data-testid="continue-program-btn"
                >
                  Continuar estudiando <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Choose Program */}
            {!user?.program && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="choose-program-card">
                <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Elige tu programa</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {programs.map((prog) => (
                    <div key={prog.program_id} className="border border-slate-200 rounded-xl p-4 hover:border-[#20D3C0] transition-colors cursor-pointer"
                      onClick={() => navigate(`/payment?program=${prog.program_id}`)}
                      data-testid={`choose-program-${prog.program_id}`}
                    >
                      <h4 className="font-semibold text-[#193B6E] mb-1">{prog.name}</h4>
                      <p className="text-xs text-slate-500 mb-3">{prog.institution}</p>
                      <button className="text-sm text-[#20D3C0] font-medium flex items-center gap-1">
                        Inscribirme <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription upgrade */}
            {user?.subscription_status !== "active" && (
              <div className="bg-gradient-to-r from-[#193B6E] to-[#112C54] rounded-xl p-6 text-white" data-testid="upgrade-banner">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Hazte miembro</h3>
                    <p className="text-slate-300 text-sm">Accede a todo el contenido sin publicidad y con clases en vivo</p>
                  </div>
                  <Link to="/payment" className="bg-[#20D3C0] text-[#112C54] font-semibold rounded-full px-6 py-2 hover:bg-[#1AB8A6] transition-all text-sm whitespace-nowrap" data-testid="upgrade-btn">
                    Actualizar
                  </Link>
                </div>
              </div>
            )}

            {/* Recent Quiz Results */}
            {quizHistory.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Ultimos cuestionarios</h3>
                <div className="space-y-3">
                  {quizHistory.slice(0, 5).map((attempt) => (
                    <div key={attempt.attempt_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-[#193B6E]">{attempt.quiz_id}</p>
                        <p className="text-xs text-slate-400">{new Date(attempt.submitted_at).toLocaleDateString('es-MX')}</p>
                      </div>
                      <span className={`text-sm font-bold ${attempt.score >= 70 ? 'text-green-500' : 'text-red-500'}`}>
                        {attempt.score}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referral System */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="referral-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[#20D3C0]/10 p-2 rounded-lg">
                  <Gift className="w-5 h-5 text-[#20D3C0]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>Invita amigos y gana descuentos</h3>
                  <p className="text-xs text-slate-500">10% de descuento por cada amigo que se inscriba</p>
                </div>
              </div>

              {referralData && (
                <>
                  {/* My referral code */}
                  <div className="bg-[#F8FAFC] rounded-xl p-4 mb-4">
                    <p className="text-xs text-slate-500 mb-2">Tu codigo de referido</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white rounded-lg border-2 border-dashed border-[#20D3C0] px-4 py-3 text-center">
                        <span className="text-xl font-bold text-[#193B6E] tracking-widest font-mono" data-testid="referral-code">
                          {referralData.referral_code}
                        </span>
                      </div>
                      <button
                        onClick={copyReferralCode}
                        className="bg-[#20D3C0]/10 text-[#193B6E] p-3 rounded-lg hover:bg-[#20D3C0]/20 transition-colors"
                        title="Copiar codigo"
                        data-testid="copy-referral-btn"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={shareReferralLink}
                        className="bg-[#193B6E] text-white p-3 rounded-lg hover:bg-[#132D55] transition-colors"
                        title="Compartir"
                        data-testid="share-referral-btn"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                      <p className="text-lg font-bold text-[#193B6E]">{referralData.total_referrals}</p>
                      <p className="text-xs text-slate-500">Invitados</p>
                    </div>
                    <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                      <p className="text-lg font-bold text-green-500">{referralData.successful_referrals}</p>
                      <p className="text-xs text-slate-500">Activos</p>
                    </div>
                    <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                      <p className="text-lg font-bold text-[#20D3C0]">{Math.min(referralData.discount_earned, 50)}%</p>
                      <p className="text-xs text-slate-500">Descuento</p>
                    </div>
                  </div>

                  {/* Referral list */}
                  {myReferrals.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-[#193B6E] mb-2 flex items-center gap-1">
                        <Users className="w-4 h-4" /> Tus referidos
                      </p>
                      <div className="space-y-2">
                        {myReferrals.slice(0, 5).map((ref) => (
                          <div key={ref.referral_id} className="flex items-center justify-between py-2 px-3 bg-[#F8FAFC] rounded-lg text-sm">
                            <span className="text-[#193B6E]">{ref.referred_name || ref.referred_email}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ref.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                              {ref.status === 'completed' ? 'Activo' : 'Pendiente'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Apply someone else's code */}
              {!user?.referred_by && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-sm text-slate-500 mb-2">Tienes un codigo de alguien mas?</p>
                  <div className="flex gap-2">
                    <Input
                      value={referralInput}
                      onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                      placeholder="Ingresa el codigo"
                      className="font-mono uppercase"
                      data-testid="apply-referral-input"
                    />
                    <button
                      onClick={handleApplyReferral}
                      disabled={applyingCode || !referralInput.trim()}
                      className="bg-[#20D3C0] text-[#112C54] font-semibold rounded-lg px-4 py-2 text-sm hover:bg-[#1AB8A6] transition-all disabled:opacity-50 whitespace-nowrap"
                      data-testid="apply-referral-btn"
                    >
                      {applyingCode ? "..." : "Aplicar"}
                    </button>
                  </div>
                </div>
              )}
              {user?.referred_by && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Ya tienes un 10% de descuento por referido
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="events-sidebar">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Proximos eventos</h3>
              {events.length === 0 ? (
                <p className="text-sm text-slate-400">No hay eventos programados</p>
              ) : (
                <div className="space-y-3">
                  {events.slice(0, 3).map((evt) => (
                    <div key={evt.event_id} className="p-3 bg-[#F8FAFC] rounded-lg border border-slate-100">
                      <p className="text-sm font-medium text-[#193B6E]">{evt.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {new Date(evt.scheduled_at).toLocaleDateString('es-MX', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/events" className="inline-flex items-center gap-1 mt-4 text-[#20D3C0] font-medium text-sm hover:underline" data-testid="view-all-events">
                Ver todos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Quick links */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Accesos rapidos</h3>
              <div className="space-y-2">
                <Link to="/free" className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors" data-testid="quick-free">
                  <PlayCircle className="w-5 h-5 text-[#20D3C0]" />
                  <span className="text-sm text-[#193B6E]">Contenido gratuito</span>
                </Link>
                <Link to="/events" className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors">
                  <Calendar className="w-5 h-5 text-[#20D3C0]" />
                  <span className="text-sm text-[#193B6E]">Calendario de clases</span>
                </Link>
                <a href="https://wa.me/529981851150" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors">
                  <Award className="w-5 h-5 text-[#20D3C0]" />
                  <span className="text-sm text-[#193B6E]">Soporte WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
